import { lazy, Suspense, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import { Bot, CheckCircle2, FlaskConical, PencilLine, RefreshCw, RotateCcw, ShieldAlert, Workflow, XCircle } from "lucide-react";
import { ApiError, useApi, type AiProposal, type Citation } from "@ncp/api-client";
import { useLocale, useTranslation, useEnumLabel } from "@ncp/i18n";
import { Button, Dialog, DialogContent, useConfirm, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Field, MarkdownBody, ProposalCard, SourceDrawer, Textarea, Tooltip, cn, hasArabicBody, useSave, notifySaveFailed, SaveError, StepLetterSheets, useLetterPreview, useMicrocopy, type StepLettersAnswer } from "@ncp/ui";
import type { RichTextEditorHandle } from "@ncp/ui/components/rich-text-editor";

// TipTap / ProseMirror load when a proposal is first edited, not with every conversation (shell-ia §18 budget; WP-SHELL request).
const RichTextEditor = lazy(() => import("@ncp/ui/components/rich-text-editor").then((m) => ({ default: m.RichTextEditor })));
import { RedraftAction } from "./redraft/RedraftDialog";
import { SourceDataPanel, sourceDataOf } from "./DataViews";
import { RedlinePanel, redlinesOf } from "./Redlines";
import { AiUsePanel, FigureCheckPanel } from "./FigureCheck";

const IRREVERSIBLE_ACTIONS = new Set(["correspondence.send", "rfi.create", "matter.rfi", "ec.publish", "databank.merge"]);
/** The shortest reasons the platform accepts on a draft of a high-risk function. */
export const REASONS_MIN = 40;

/** `lettersFingerprint`: the letters the approval's step sends, as read beside the draft (release 1.6); none when it takes no step that sends any. */
interface ReviewVars { id: string; action: "approve" | "reject" | "revise"; note?: string; edited?: string; lettersFingerprint?: string | null }

/**
 * A draft whose approval sends letters of its own accord: one that takes a workflow step, or a request for information (its covering letter, and
 * the step it may take). Its card shows those letters, in full, beside the draft (release 1.6).
 */
const takesStep = (p: Pick<AiProposal, "status" | "caseId" | "proposedAction">) => p.status === "Proposed" && !!p.caseId && (p.proposedAction?.type === "workflow.transition" || p.proposedAction?.type === "rfi.create");
/** The letters an approval sends, with the text as the officer approves it (their edits, when they edit and approve). */
const readApprovalLetters = (api: ReturnType<typeof useApi>, id: string, edited?: string) =>
  edited ? api.post<StepLettersAnswer>(`/api/ai/proposals/${id}/letters`, { editedContentMarkdown: edited }) : api.get<StepLettersAnswer>(`/api/ai/proposals/${id}/letters`);
const LETTER_REFUSALS = new Set(["LettersNotRead", "LettersChanged"]);
/** Why an approval was refused: the draft was not opened, or a warning or a finding of the platform needs an authorised officer's override. */
interface ApprovalRefusal { code: "NotOpened" | "OverrideRequired" | string; message: string; overrideKind?: string | null; subjectKey?: string | null; finding?: string | null; findingAr?: string | null }
interface ReviewResponse { succeeded: boolean; error?: string; actionResult?: string; refusal?: ApprovalRefusal | null }
/** The shortest reasons an override of a finding is recorded with. */
const OVERRIDE_MIN = 10;

/**
 * Records that the officer opened a draft's full text and, with `sources`, its sources (Condition 3.10): every draft is approved only once its
 * approver has read it to the end and opened the sources it cites, and the time between opening and approving is kept. Every surface records
 * the text when its end has been on screen and the sources when one is opened – the review card, and the tabs that lay a draft out through
 * <DraftTextEnd> and <DraftSources> – never on loading. Once per draft and part per page; drafts with no file are recorded too.
 */
export function useMarkOpened() {
  const api = useApi();
  const sent = useRef(new Set<string>());
  // A source opened before the text has been read to its end on this page waits for that reading: opening a source never counts as reading the text.
  const waiting = useRef(new Set<string>());
  const mark = (p: Pick<AiProposal, "id" | "status" | "caseId">, options: { sources?: boolean } = {}) => {
    const sources = options.sources ?? true;
    const key = `${p.id}:${sources ? "sources" : "text"}`;
    if (p.status !== "Proposed" || sent.current.has(key)) return;
    if (sources && !sent.current.has(`${p.id}:text`)) { waiting.current.add(p.id); return; }
    sent.current.add(key);
    void api.post(`/api/ai/proposals/${p.id}/opened`, { sources })
      .then(() => { if (!sources && waiting.current.delete(p.id)) mark(p, { sources: true }); })
      .catch(() => sent.current.delete(key));
  };
  return mark;
}

/**
 * Approve / edit-and-approve / reject / revise flow shared by every surface that reviews AI proposals: the back-office inbox and case
 * workspace, the console's test bench, and the AI Workspace's chat and cross-workspace Inbox. `invalidate` lists the query keys that
 * show the proposal so they refresh after a decision.
 */
export function useProposalReview(invalidate: readonly QueryKey[]) {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<AiProposal | null>(null);
  const [rejecting, setRejecting] = useState<AiProposal | null>(null);
  const [revising, setRevising] = useState<AiProposal | null>(null);
  const [reasoning, setReasoning] = useState<AiProposal | null>(null);
  const [overriding, setOverriding] = useState<{ proposal: AiProposal; refusal: ApprovalRefusal; vars: ReviewVars } | null>(null);
  const refused = (r: ReviewResponse | undefined, vars: ReviewVars): boolean => {
    const refusal = r?.refusal;
    if (refusal?.code !== "OverrideRequired" || vars.action !== "approve") return false;
    const proposal = [editing, reasoning].find((p) => p?.id === vars.id) ?? lastApproved.current;
    if (!proposal) return false;
    setOverriding({ proposal, refusal, vars });
    return true;
  };
  const lastApproved = useRef<AiProposal | null>(null);
  const review = useSave({
    mutationFn: async ({ id, action, note, edited, lettersFingerprint }: ReviewVars) => api.post<ReviewResponse>(`/api/ai/proposals/${id}/${action}`, { note, editedContentMarkdown: edited, lettersFingerprint }),
    saved: (r, vars) => ({ title: vars.action === "approve" ? t("ai.approved") : vars.action === "revise" ? t("ai.revisionRequested") : t("ai.rejected"), description: r.actionResult ?? undefined }),
    onSuccess: () => {
      for (const key of invalidate) void qc.invalidateQueries({ queryKey: key });
      setEditing(null); setRejecting(null); setRevising(null); setReasoning(null); setOverriding(null); setReading(null);
    },
    // A refused approval an authorised officer may override opens the override (the answer said `succeeded: false`, or the API refused it
    // with the same body); any other failure is reported with its cause.
    report: "silent",
    onError: (e: Error, vars) => {
      const body = e instanceof SaveError ? (e.result as ReviewResponse | undefined) : e instanceof ApiError ? (e.problem as unknown as ReviewResponse | undefined) : undefined;
      if (refused(body, vars)) return;
      // Letters refused as unread or changed since they were read are read again before the approval is given again (release 1.6).
      if (e instanceof ApiError && LETTER_REFUSALS.has(e.problem?.code ?? "")) { lettersShown.current.delete(vars.id); void qc.invalidateQueries({ queryKey: ["step-letter-preview"] }); }
      notifySaveFailed(e);
    },
  });
  // The letters each draft's card showed beside it, by their fingerprint: an approval carries them, and one whose letters were not shown, or have
  // changed since, puts them to the officer first (release 1.6).
  const lettersShown = useRef(new Map<string, string | null>());
  const markLettersShown = (id: string, fingerprint: string | null) => { lettersShown.current.set(id, fingerprint); };
  const [reading, setReading] = useState<{ proposal: AiProposal; edited?: string; note?: string } | null>(null);
  // An authorised officer's override of the warning or finding holding the approval, then the approval itself (Conditions 2.21, 3.5).
  const recordOverride = useSave({
    // A draft on a file is overridden on the file; a draft attached to no file (Deal Watch, advocacy, a market study) on the draft itself.
    mutationFn: (reason: string) => overriding!.proposal.caseId
      ? api.post(`/api/cases/${overriding!.proposal.caseId}/review/overrides`, { kind: overriding!.refusal.overrideKind, subjectKey: overriding!.refusal.subjectKey, reason })
      : api.post(`/api/ai/proposals/${overriding!.proposal.id}/overrides`, { kind: overriding!.refusal.overrideKind, reason }),
    saved: t("backoffice:r3Process.finding.recorded", { defaultValue: "Override recorded" }),
    onSuccess: () => { const vars = overriding!.vars; review.mutate(vars); },
  });
  // Approvals whose action reaches outside the ministry (a letter, a request to a party, a published notice) or cannot be undone ask first.
  const confirm = useConfirm();
  const { locale } = useLocale();
  const approve = async (p: AiProposal, edited?: string, note?: string) => {
    lastApproved.current = p;
    if (p.reasonsRequired && (note ?? "").trim().length < REASONS_MIN) { setReasoning(p); return; }
    const action = p.proposedAction;
    if (action && IRREVERSIBLE_ACTIONS.has(action.type)) {
      const ok = await confirm({
        title: t("ai.review.confirmTitle"),
        description: t("ai.review.confirmBody", { action: locale === "ar" && action.labelAr ? action.labelAr : action.label }),
        confirmLabel: t("ai.review.approveAndExecute"),
      });
      if (!ok) return;
    }
    let lettersFingerprint: string | null = null;
    if (takesStep(p)) {
      let answer: StepLettersAnswer;
      try { answer = await qc.fetchQuery({ queryKey: ["proposal-letters", p.id, edited ?? null], queryFn: () => readApprovalLetters(api, p.id, edited), staleTime: 0 }); }
      catch (e) { notifySaveFailed(e); return; }
      if (answer.letters.length > 0) {
        if (lettersShown.current.get(p.id) !== answer.fingerprint) { setReading({ proposal: p, edited, note }); return; }
        lettersFingerprint = answer.fingerprint ?? null;
      }
    }
    review.mutate({ id: p.id, action: "approve", edited, note, lettersFingerprint });
  };
  const labels = useMemo(() => ({ approve: t("actions.approve"), edit: t("actions.editAndApprove"), reject: t("actions.reject"), revise: t("actions.requestRevision"), proposedAction: t("ai.review.proposedAction"), humanInLoop: t("ai.humanInLoop"), confidence: t("ai.confidence") }), [t]);

  const dialogs = (
    <>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="xl">
          <DialogHeader>
            <DialogTitle><PencilLine className="inline size-4 me-2" />{t("actions.editAndApprove")}</DialogTitle>
            <DialogDescription>{editing?.title}</DialogDescription>
          </DialogHeader>
          {editing ? <EditForm proposal={editing} busy={review.isPending} onSubmit={(edited, note) => void approve(editing, edited, note)} onCancel={() => setEditing(null)} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t("actions.reject")}</DialogTitle>
            <DialogDescription>{rejecting?.title}</DialogDescription>
          </DialogHeader>
          {rejecting ? <RejectForm busy={review.isPending} draft={`proposal:${rejecting.id}:reject:note`} onSubmit={(note) => review.mutate({ id: rejecting.id, action: "reject", note })} onCancel={() => setRejecting(null)} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!revising} onOpenChange={(o) => !o && setRevising(null)}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle><RefreshCw className="inline size-4 me-2" />{t("actions.requestRevision")}</DialogTitle>
            <DialogDescription>{t("ai.review.reviseHint")}</DialogDescription>
          </DialogHeader>
          {revising ? <RejectForm busy={review.isPending} revise draft={`proposal:${revising.id}:revise:note`} onSubmit={(note) => review.mutate({ id: revising.id, action: "revise", note })} onCancel={() => setRevising(null)} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reasoning} onOpenChange={(o) => !o && setReasoning(null)}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle><CheckCircle2 className="inline size-4 me-2" />{t("ai.review.reasonsTitle")}</DialogTitle>
            <DialogDescription>{reasoning?.title}</DialogDescription>
          </DialogHeader>
          {reasoning ? <ReasonsForm busy={review.isPending} draft={`proposal:${reasoning.id}:approve:reasons`} onSubmit={(note) => void approve(reasoning, undefined, note)} onCancel={() => setReasoning(null)} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent size="xl">
          {reading ? <ApprovalLetters proposal={reading.proposal} edited={reading.edited} busy={review.isPending} onCancel={() => setReading(null)}
            onConfirm={(fingerprint) => { lettersShown.current.set(reading.proposal.id, fingerprint); review.mutate({ id: reading.proposal.id, action: "approve", edited: reading.edited, note: reading.note, lettersFingerprint: fingerprint }); }} /> : null}
        </DialogContent>
      </Dialog>
      <Dialog open={!!overriding} onOpenChange={(o) => !o && setOverriding(null)}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle><ShieldAlert className="inline size-4 me-2" />{t("backoffice:r3Process.finding.title", { defaultValue: "Finding of the platform" })}</DialogTitle>
            <DialogDescription dir="auto">{overriding ? (locale === "ar" ? overriding.refusal.findingAr ?? overriding.refusal.message : overriding.refusal.finding ?? overriding.refusal.message) : null}</DialogDescription>
          </DialogHeader>
          {overriding ? <OverrideForm busy={recordOverride.isPending || review.isPending} draft={`proposal:${overriding.proposal.id}:override:${overriding.refusal.subjectKey ?? overriding.refusal.overrideKind ?? "finding"}:reason`} onSubmit={(reason) => recordOverride.mutate(reason)} onCancel={() => setOverriding(null)} /> : null}
        </DialogContent>
      </Dialog>
    </>
  );

  return { review, approve, labels, setEditing, setRejecting, setRevising, dialogs, markLettersShown };
}

/** The letters a draft's approval sends, read for the draft as it now stands – with the officer's edited text when they edit and approve (release 1.6). */
function useProposalLetters(proposal: AiProposal, edited?: string) {
  const api = useApi();
  return useLetterPreview(takesStep(proposal) ? `proposal:${proposal.id}` : null, { id: proposal.id, edited: edited ?? null }, (r) => readApprovalLetters(api, r.id, r.edited ?? undefined));
}

/** Beside the draft: the letters its approval sends, in full. */
function ProposalLetters({ proposal, onShown }: { proposal: AiProposal; onShown: (id: string, fingerprint: string | null) => void }) {
  const m = useMicrocopy();
  const preview = useProposalLetters(proposal);
  useEffect(() => { if (preview.shown && preview.letters.length > 0) onShown(proposal.id, preview.fingerprint); }, [preview.shown, preview.fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps
  return <StepLetterSheets preview={preview} hint={m("stepLettersApprovalHint")} className="mt-4" />;
}

/** An approval whose letters were not shown beside the draft, or changed since: the letters in full, approved only once they are on screen. */
function ApprovalLetters({ proposal, edited, busy, onConfirm, onCancel }: { proposal: AiProposal; edited?: string; busy: boolean; onConfirm: (fingerprint: string | null) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const m = useMicrocopy();
  const preview = useProposalLetters(proposal, edited);
  return (
    <>
      <DialogHeader>
        <DialogTitle>{m("stepLettersConfirmTitle")}</DialogTitle>
        <DialogDescription>{m("stepLettersApprovalHint")}</DialogDescription>
      </DialogHeader>
      <div className="max-h-[60vh] overflow-y-auto"><StepLetterSheets preview={preview} hint={m("stepLettersApprovalHint")} /></div>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
        <Button variant="gold" loading={busy} disabled={!preview.shown} onClick={() => onConfirm(preview.fingerprint)}><CheckCircle2 /> {m("stepLettersConfirm")}</Button>
      </DialogFooter>
    </>
  );
}

/**
 * Arabic reviewers read proposals in Arabic: titles and summaries are rendered as soon as the list shows, full bodies when a draft is
 * expanded. Renderings are cached on the proposal by the API, so each text is translated at most once.
 */
function useProposalTranslation(proposals: AiProposal[], invalidate: readonly QueryKey[]) {
  const api = useApi();
  const qc = useQueryClient();
  const { locale } = useLocale();
  const requested = useRef(new Set<string>());
  const translate = async (id: string, full: boolean) => {
    const key = `${id}:${full ? "full" : "summary"}`;
    if (requested.current.has(key)) return;
    requested.current.add(key);
    try {
      await api.post(`/api/ai/proposals/${id}/translate?full=${full}`);
      for (const k of invalidate) void qc.invalidateQueries({ queryKey: k });
    } catch {
      requested.current.delete(key);
    }
  };
  useEffect(() => {
    if (locale !== "ar") return;
    const missing = proposals.filter((p) => !p.modelId.startsWith("offline") && (!p.titleAr || !p.summaryAr)).slice(0, 12);
    let cancelled = false;
    void (async () => {
      for (const p of missing) {
        if (cancelled) return;
        await translate(p.id, false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, proposals.map((p) => `${p.id}:${p.titleAr ? 1 : 0}${p.summaryAr ? 1 : 0}`).join("|")]);
  return (id: string, full: boolean) => { void translate(id, full); };
}

export interface ProposalListProps {
  proposals: AiProposal[];
  invalidate: readonly QueryKey[];
  onOpenRun?: (runId: string) => void;
  /** When given, cards carry a "View <case number>" button that hands the case to the host (router navigation or a deep link). */
  onOpenCase?: (caseId: string, proposal: AiProposal) => void;
  defaultOpenSingle?: boolean;
  /** A proposal to open and scroll to (deep link `?proposal=`). */
  focusId?: string | null;
  /** Hide the model / tokens / golden-dataset footer (compact chat cards). */
  compact?: boolean;
  /** Extra content per card, rendered in the footer row (e.g. the conversation the proposal came from). */
  extra?: (proposal: AiProposal) => ReactNode;
  /** Resolves a workflow state key to a display label (shown as a step tag on each card and as group headers). */
  stepLabel?: (state: string) => string | null | undefined;
  /** Group the cards under a header per workflow step, in the order given by `stepOrder` (unknown/manual steps last). */
  groupByStep?: boolean;
  stepOrder?: string[];
}

export function ProposalList({ proposals, invalidate, onOpenRun, onOpenCase, defaultOpenSingle = true, focusId = null, compact = false, extra, stepLabel, groupByStep = false, stepOrder = [] }: ProposalListProps) {
  const { t } = useTranslation();
  const [citation, setCitation] = useState<Citation | null>(null);
  const { approve, labels, setEditing, setRejecting, setRevising, dialogs, markLettersShown } = useProposalReview(invalidate);
  const api = useApi();
  const enumLabel = useEnumLabel();
  const { locale } = useLocale();
  const translate = useProposalTranslation(proposals, invalidate);
  // The draft counts as opened once its full text has been read to the end on screen, its sources once one of them is opened (Condition 3.10).
  const markOpened = useMarkOpened();
  // Reviewers can promote any live proposal into the "recorded runs" golden dataset of the evaluation harness.
  const record = useSave({
    mutationFn: (id: string) => api.post<{ name: string }>(`/api/governance/evals/record/${id}`),
    saved: (r) => ({ title: t("ai.recordedAsGolden"), description: r.name }),
  });

  const groups = useMemo(() => {
    if (!groupByStep) return [{ key: null as string | null, items: proposals }];
    const byStep = new Map<string | null, AiProposal[]>();
    for (const p of proposals) {
      const k = p.workflowState ?? null;
      const list = byStep.get(k) ?? [];
      list.push(p);
      byStep.set(k, list);
    }
    const rank = (k: string | null) => { if (k === null) return Number.MAX_SAFE_INTEGER; const i = stepOrder.indexOf(k); return i === -1 ? Number.MAX_SAFE_INTEGER - 1 : i; };
    return [...byStep.entries()].sort((a, b) => rank(a[0]) - rank(b[0])).map(([key, items]) => ({ key, items }));
  }, [groupByStep, proposals, stepOrder]);

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(`proposal-${focusId}`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusId, proposals.length]);

  const renderCard = (p: AiProposal) => (
    <div key={p.id} id={`proposal-${p.id}`} className={cn(focusId === p.id && "rounded-2xl ring-2 ring-aegold-300 ring-offset-2")}>
    <ProposalCard
      proposal={{ ...p, proposedAction: p.proposedAction ?? null }}
      labels={labels}
      defaultOpen={focusId === p.id || (defaultOpenSingle && proposals.length === 1)}
      onApprove={p.status === "Proposed" ? () => void approve(p) : undefined}
      onEdit={p.status === "Proposed" ? () => setEditing(p) : undefined}
      onReject={p.status === "Proposed" ? () => setRejecting(p) : undefined}
      onRevise={p.status === "Proposed" && !p.modelId.startsWith("offline") ? () => setRevising(p) : undefined}
      onOpenCitation={(c) => { setCitation(c as Citation); markOpened(p, { sources: true }); }}
      onReadToEnd={() => markOpened(p, { sources: false })}
      onToggle={(open) => { if (open && locale === "ar" && !hasArabicBody(p)) translate(p.id, true); }}
      formatKind={(k) => enumLabel("proposalKind", k)}
      formatFlag={(f) => enumLabel("guardrail", f)}
      formatStatus={(st) => enumLabel("proposalStatus", st)}
      formatStep={groupByStep ? undefined : stepLabel}
    >
      {takesStep(p) ? <ProposalLetters proposal={p} onShown={markLettersShown} /> : null}
      <ProposalSourceData structured={p.structured} />
      <ProposalRedlines structured={p.structured} />
      {p.figureCheck?.total ? <FigureCheckPanel check={p.figureCheck} className="mt-4" /> : null}
      {p.aiUse ? <AiUsePanel record={p.aiUse} className="mt-3" /> : null}
      {compact ? extra?.(p) ?? null : (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          {/* The AI that made the draft, by the platform's neutral name (condition 3.1); no token count or other technical figure (visual item 11). */}
          <span>{t("ai.modelUsed")}: <span className="font-mono">{p.modelId}</span></span>
          {p.reviewerName ? <><span>·</span><span>{t("ai.reviewedBy")}: {p.reviewerName}</span></> : null}
          {extra ? <span className="flex items-center gap-1">{extra(p)}</span> : null}
          <span className="ms-auto flex gap-2">
            {!p.modelId.startsWith("offline") ? <Button size="sm" variant="ghost" loading={record.isPending && record.variables === p.id} onClick={() => record.mutate(p.id)}><FlaskConical /> {t("ai.saveAsGolden")}</Button> : null}
            {onOpenRun ? <Button size="sm" variant="ghost" onClick={() => onOpenRun(p.runId)}><Bot /> {t("ai.agentActivity")}</Button> : null}
            {onOpenCase && p.caseId ? <Button size="sm" variant="outline" onClick={() => onOpenCase(p.caseId!, p)}>{t("actions.view")} {p.caseNumber}</Button> : null}
          </span>
        </div>
      )}
    </ProposalCard>
    </div>
  );

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={g.key ?? "__none"} className="space-y-4">
          {groupByStep ? (
            <div className="flex items-center gap-2 pt-1">
              <Workflow className="size-3.5 text-aegold-700" />
              <p className="text-xs font-semibold uppercase tracking-wider text-sand-700">{g.key ? (stepLabel?.(g.key) ?? g.key) : t("ai.review.manualRuns")}</p>
              <span className="h-px flex-1 bg-sand-200" />
              <span className="text-[11px] text-muted-foreground tabular">{g.items.length}</span>
            </div>
          ) : null}
          {g.items.map(renderCard)}
        </div>
      ))}
      <SourceDrawer citation={citation} onClose={() => setCitation(null)} />
      {dialogs}
    </div>
  );
}

function ProposalSourceData({ structured }: { structured: AiProposal["structured"] }) {
  const entries = useMemo(() => sourceDataOf(structured), [structured]);
  return entries.length ? <SourceDataPanel entries={entries} className="mt-4" /> : null;
}

function ProposalRedlines({ structured }: { structured: AiProposal["structured"] }) {
  const entries = useMemo(() => redlinesOf(structured), [structured]);
  return entries.length ? <RedlinePanel entries={entries} className="mt-4" /> : null;
}

/**
 * Edit-and-approve: the agent's draft on one side as it will read, the officer's version on the other in a formatted editor. The
 * officer corrects by hand, asks the assistant to redraft ("shorter", "cite Article 10 explicitly") or resets to the draft; what is
 * approved is exactly what the editor shows, and the edit distance to the draft is kept as a signal on the agent.
 */
function EditForm({ proposal, busy, onSubmit, onCancel }: { proposal: AiProposal; busy: boolean; onSubmit: (edited: string, note: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const enumLabel = useEnumLabel();
  const editorRef = useRef<RichTextEditorHandle>(null);
  const [content, setContent] = useState(proposal.contentMarkdown);
  const [note, setNote] = useState("");
  const changed = content.trim() !== proposal.contentMarkdown.trim();
  const draftLabelId = useId();
  const versionLabelId = useId();
  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-1.5">
          <p id={draftLabelId} className="flex items-center gap-1.5 text-sm font-medium leading-none text-seablue-900"><Bot className="size-3.5" /> {t("ai.review.aiDraft")}</p>
          <div aria-labelledby={draftLabelId} className="max-h-[28rem] min-h-[28rem] overflow-y-auto rounded-lg border border-seablue-200 bg-seablue-50/40 p-3 scrollbar-thin"><MarkdownBody className="prose-sm" citations={proposal.citations}>{proposal.contentMarkdown}</MarkdownBody></div>
        </div>
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <p id={versionLabelId} className="flex items-center gap-1.5 text-sm font-medium leading-none text-aegold-900"><PencilLine className="size-3.5" /> {t("ai.review.yourVersion")}</p>
            <span className="text-[11px] text-muted-foreground">{changed ? t("ai.review.editHintChanged") : t("ai.review.editHintNoChanges")}</span>
          </div>
          <Suspense fallback={<div className="min-h-[25.5rem] animate-pulse rounded-lg border border-sand-200 bg-sand-50/60" />}>
          <RichTextEditor
            ref={editorRef}
            value={content}
            onChange={setContent}
            draft={`proposal:${proposal.id}:edit:content`}
            minHeightClassName="min-h-[25.5rem]"
            contentClassName="max-h-[25.5rem] overflow-y-auto"
            aria-labelledby={versionLabelId}
            actions={(
              <>
                {changed ? <Tooltip content={t("ai.review.resetToDraft")}><Button size="icon-sm" variant="ghost" className="size-7" aria-label={t("ai.review.resetToDraft")} onClick={() => editorRef.current?.setMarkdown(proposal.contentMarkdown)}><RotateCcw className="size-3.5" /></Button></Tooltip> : null}
                <RedraftAction getText={() => content} onApply={(text) => editorRef.current?.setMarkdown(text)} context={{ caseId: proposal.caseId, kind: `An AI draft awaiting the officer's approval (${enumLabel("proposalKind", proposal.kind)})`, title: proposal.title }} />
              </>
            )}
          />
          </Suspense>
        </div>
      </div>
      {proposal.reasonsRequired ? (
        <Field label={t("ai.review.reasonsLabel")} required hint={t("ai.review.reasonsHint", { count: REASONS_MIN, typed: note.trim().length })}>{(id) => <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={3} draft={`proposal:${proposal.id}:edit:note`} />}</Field>
      ) : (
        <Field label={t("ai.review.reviewNote")}>{(id) => <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={2} draft={`proposal:${proposal.id}:edit:note`} />}</Field>
      )}
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
        <Button variant="gold" loading={busy} disabled={proposal.reasonsRequired && note.trim().length < REASONS_MIN} onClick={() => onSubmit(content, note)}><CheckCircle2 /> {t("ai.review.approveAndExecute")}</Button>
      </DialogFooter>
    </div>
  );
}

/** A draft that bears on the outcome of a case is approved with the officer's own reasons: what they checked and what the finding rests on. */
function ReasonsForm({ busy, draft, onSubmit, onCancel }: { busy: boolean; draft?: string; onSubmit: (note: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  return (
    <div className="space-y-4">
      <Field label={t("ai.review.reasonsLabel")} required hint={t("ai.review.reasonsHint", { count: REASONS_MIN, typed: note.trim().length })}>{(id) => <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={4} draft={draft} />}</Field>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
        <Button variant="gold" loading={busy} disabled={note.trim().length < REASONS_MIN} onClick={() => onSubmit(note)}><CheckCircle2 /> {t("actions.approve")}</Button>
      </DialogFooter>
    </div>
  );
}

/** The authorised officer's reasons for overriding what holds the approval; they go into the file's history and the decision memo. */
function OverrideForm({ busy, draft, onSubmit, onCancel }: { busy: boolean; draft?: string; onSubmit: (reason: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  return (
    <div className="space-y-4">
      <Field label={t("backoffice:r3Process.finding.reason", { defaultValue: "Reasons" })} required hint={t("backoffice:r3Process.finding.reasonHint", { defaultValue: "Goes into the file's history and the decision memo." })}>{(id) => <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} rows={4} draft={draft} />}</Field>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
        <Button variant="gold" loading={busy} disabled={reason.trim().length < OVERRIDE_MIN} onClick={() => onSubmit(reason.trim())}><ShieldAlert /> {t("backoffice:r3Process.finding.recordAndApprove", { defaultValue: "Override and approve" })}</Button>
      </DialogFooter>
    </div>
  );
}

function RejectForm({ busy, revise = false, draft, onSubmit, onCancel }: { busy: boolean; revise?: boolean; draft?: string; onSubmit: (note: string) => void; onCancel: () => void }) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  return (
    <div className="space-y-4">
      <Field label={t("ai.review.reviewNoteRequired")} required hint={revise ? t("ai.review.reviseNoteHint") : t("ai.review.rejectNoteHint")}>{(id) => <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} rows={4} draft={draft} />}</Field>
      <DialogFooter>
        <Button variant="ghost" onClick={onCancel}>{t("actions.cancel")}</Button>
        {revise
          ? <Button variant="gold" loading={busy} disabled={note.trim().length < 3} onClick={() => onSubmit(note)}><RefreshCw /> {t("actions.requestRevision")}</Button>
          : <Button variant="destructive" loading={busy} disabled={note.trim().length < 3} onClick={() => onSubmit(note)}><XCircle /> {t("actions.reject")}</Button>}
      </DialogFooter>
    </div>
  );
}
